import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import SimplePageHero from '../marketing/components/SimplePageHero';
import { InitialsAvatar } from '../marketing/components/icons';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const icon = (p: React.ReactNode) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    {p}
  </svg>
);

const segments = [
  { title: 'Portfolio landlords', body: 'Track compliance and cash across dozens of units without a single spreadsheet.', icon: icon(<><path d="M3 21h18" /><path d="M5 21V8l7-4 7 4v13" /></>) },
  { title: 'Letting agents', body: 'Onboard managed properties in minutes and prove compliance to every client.', icon: icon(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></>) },
  { title: 'HMO operators', body: 'Room-level rent tracking and HMO-specific fire and licence workflows built in.', icon: icon(<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></>) },
  { title: 'BTL investors', body: 'Model yield, ROI and tax, then track the reality against the model in one place.', icon: icon(<><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></>) },
];

const stats = [
  { v: '10,000+', l: 'Certificates managed' },
  { v: '£500M+', l: 'Property assets tracked' },
  { v: '2,400', l: 'Active agencies' },
  { v: '99.9%', l: 'Reminder accuracy' },
];

const quotes = [
  { quote: "MillOS caught two Gas Safety certs about to lapse before I'd even noticed.", name: 'James Whitfield', role: 'Portfolio landlord · 34 units' },
  { quote: 'Onboarding a new managed property takes minutes now instead of an afternoon.', name: 'Priya Ramesh', role: 'Letting agent · Manchester' },
  { quote: 'Per-room rent tracking via Open Banking replaced three spreadsheets.', name: 'Daniel Okafor', role: 'HMO operator · Leeds' },
];

const CustomersPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'Customers — Who Uses MillOS | MillOS',
    description: 'Portfolio landlords, letting agents, HMO operators and buy-to-let investors across the UK use MillOS to stay compliant and financially organised.',
    canonicalPath: '/customers',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <SimplePageHero
        eyebrow="Customers"
        title={<>Built for every kind of <Box component="em" sx={{ fontStyle: 'italic' }}>landlord</Box></>}
        body="From a first buy-to-let to a 500-unit agency, MillOS scales to how you run property."
      />
      <Box component="section" sx={{ padding: { xs: '32px 16px 20px', md: '48px 52px 20px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: '20px' }}>
          {segments.map((s) => (
            <Box key={s.title} sx={{ background: C.panel, borderRadius: '24px', padding: '28px' }}>
              <Box sx={{ width: 46, height: 46, borderRadius: '13px', background: C.accent, display: 'grid', placeItems: 'center', marginBottom: '18px' }}>{s.icon}</Box>
              <Box component="h2" sx={{ fontFamily: F.display, fontSize: 22, lineHeight: 1.1, margin: 0 }}>{s.title}</Box>
              <Box sx={{ marginTop: '10px', color: C.body, fontSize: 14, lineHeight: 1.55 }}>{s.body}</Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="section" sx={{ padding: { xs: '24px 16px', md: '40px 52px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', background: C.ink, borderRadius: '32px', padding: { xs: '36px 24px', md: '56px 40px' } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: '32px', textAlign: 'center', color: C.white }}>
            {stats.map((st) => (
              <Box key={st.l}>
                <Box sx={{ fontFamily: F.display, fontSize: 52, lineHeight: 1, letterSpacing: '-0.03em', color: C.white }}>{st.v}</Box>
                <Box sx={{ marginTop: '8px', fontSize: 13.5, color: 'rgba(255,255,255,0.65)' }}>{st.l}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box component="section" sx={{ padding: { xs: '24px 16px 64px', md: '40px 52px 96px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: '20px' }}>
          {quotes.map((q) => (
            <Box key={q.name} sx={{ background: C.panel, borderRadius: '24px', padding: '30px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Box sx={{ fontSize: 16, lineHeight: 1.6, color: C.proseText }}>&quot;{q.quote}&quot;</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: 'auto' }}>
                <InitialsAvatar name={q.name} size={44} />
                <Box>
                  <Box sx={{ fontWeight: 500, fontSize: 14 }}>{q.name}</Box>
                  <Box sx={{ fontSize: 13, color: C.muted }}>{q.role}</Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ maxWidth: 1000, margin: '40px auto 0', textAlign: 'center' }}>
          <Box
            component={RouterLink}
            to="/case-studies"
            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: C.inkSoft, color: C.white, borderRadius: '32px', padding: '13px 26px', fontSize: 16, '&:hover': { background: C.accent } }}
          >
            Read the case studies
          </Box>
        </Box>
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default CustomersPage;
